<script setup lang="ts">
import { CompanyInfoSelect, Mailing } from '@/@types/participant'
import BasicInfo from '@/components/shared/base_mailing/form/BasicInfo.vue'
import PersonalInfo from '@/components/shared/base_mailing/form/PersonalInfo.vue'
import ProfessionalInfo from '@/components/shared/base_mailing/form/ProfessionalInfo.vue'
import { Button } from '@/components/ui/button'
import { Link, useForm } from '@inertiajs/vue3'
import parsePhoneNumberFromString, { isValidPhoneNumber } from 'libphonenumber-js'
import { Loader2 } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { mailing } = defineProps<{ mailing: Mailing; company_info_select: CompanyInfoSelect; orderId: number }>()

const form = useForm({
    badge_name: mailing.badge_name || '',
    personal_info_attributes: {
        full_name: mailing.full_name,
        linkedin: mailing.linkedin || '',
        cpf: mailing.cpf || '',
        foreign_doc: mailing.foreign_doc || '',
        has_foreign_doc: mailing.has_foreign_doc || false,
        fone: mailing.fone || '',
        street: mailing.street || '',
        number: mailing.number || '',
        zip_code: mailing.zip_code || '',
        city: mailing.city || '',
        state: mailing.state || '',
        country: mailing.country || 'Brasil',
        additional_info: mailing.additional_info || '',
        another_phone: mailing.another_phone || '',
    },
    company_attributes: {
        name: mailing.company_name || '',
        area: mailing.company_area || '',
        sector: mailing.company_sector || '',
        occupation: mailing.company_occupation || '',
        hierarchical_level: mailing.company_hierarchical_level || '',
    },
})

export type FormSchema = typeof form

const { t } = useI18n()

function handleSubmit() {
    if (form.personal_info_attributes.another_phone === '+55') {
        form.reset('personal_info_attributes.another_phone')
    }

    const isFoneValid = handlePhoneValidation(form.personal_info_attributes.fone, 'personal_info_attributes.fone')
    const isAnotherValid = handlePhoneValidation(form.personal_info_attributes.another_phone, 'personal_info_attributes.another_phone', true)

    if (!isFoneValid || !isAnotherValid) {
        return
    }
    form.put(`/admin/mailings/${mailing.id}`)
}

function handlePhoneValidation(
    formPhone: string,
    fieldToSetError: 'personal_info_attributes.fone' | 'personal_info_attributes.another_phone',
    blank = false,
) {
    if (blank && formPhone.length === 0) return true

    const phoneNumber = parsePhoneNumberFromString(formPhone)

    if (formPhone.length >= 1 && formPhone.length <= 4) {
        form.setError(fieldToSetError, t('Invalid phone number'))
        form.cancel()
        return false
    }

    if (phoneNumber) {
        if (
            !isValidPhoneNumber(phoneNumber.number, {
                defaultCallingCode: phoneNumber.countryCallingCode,
                defaultCountry: phoneNumber.country,
            })
        ) {
            form.setError(fieldToSetError, t('Invalid phone number'))
            form.cancel()
            return false
        }
    }

    return true
}

const errors = computed(() => form.errors as Record<string, string>)
</script>

<template>
    <form @submit.prevent="handleSubmit">
        <div class="flex flex-col gap-8">
            <BasicInfo :form="form" />

            <PersonalInfo :form="form" :errors="errors" />

            <ProfessionalInfo :form="form" :errors="errors" :company_info_select="company_info_select" />

            <div class="flex justify-between">
                <Button :as="Link" :href="`/admin/orders/${orderId}`" class="min-w-2/12 bg-gray-200 text-slate-800 hover:bg-gray-100">
                    {{ $t('Back') }}
                </Button>

                <Button :disabled="form.processing" class="min-w-2/12">
                    <template v-if="!form.processing">
                        {{ $t('Send') }}
                    </template>
                    <template v-if="form.processing">
                        <span>
                            <Loader2 class="animate-spin" />
                            <span class="sr-only">{{ $t('Sending') }}</span>
                        </span>
                    </template>
                </Button>
            </div>
        </div>
    </form>
</template>
